/*
  Cart ID tag offer (Rebuy Smart Cart).

  Shows a "Add a Personalized Silicone Tag" row for each distinct ID tag
  matched to a collar or harness in the cart, and opens the Stylux modal
  for that tag when the row's Add button is pressed.

  Where the match comes from: every qualifying collar/harness variant has an
  active Stylux offer whose associated UPSELL product is its matched tag.
  Products without that (cat collars and harnesses, leashes, everything
  else) never render a row, so nothing here hard-codes product types.

  Rules:
  - One row per distinct matched tag, even if several items share it.
  - A tag that is already in the cart gets no row.
  - US only, matching stylux-head.liquid.

  Placement: the drawer is Rebuy's component-based Smart Cart, which is laid
  out in the Rebuy dashboard, not in snippets/rebuy-cart-template.liquid.
  The stock Stylux cart offer (#stylux-cart-offer-container) comes from a
  Rebuy custom code block there. So this script inserts its own container
  directly after the cart items on every render (Rebuy may re-render the
  drawer and drop it), and base.css hides the stock container.

  The add itself is Stylux's: modals.open writes the tag line through the
  Shopify plugin, and afterUpdateCart in stylux-head.liquid refreshes Rebuy,
  which fires rebuy:cart.change and re-renders this row.
*/
(function () {
  if (!window.Shopify || window.Shopify.country !== 'US') return;

  var CONTAINER_ID = 'mon-id-tag-offer';
  var HEADING = 'Add a Personalized Silicone Tag';
  var ACTIVE_STATES = ['ACTIVE', 'MANUALLY_SET_TO_ACTIVE'];
  var OPENING_SOURCE = 'CART_OFFER_PRIMARY_CTA';

  // variant id -> Promise<Stylux product | null>. Failed lookups are dropped
  // so the next cart change retries them.
  var productCache = new Map();
  var renderToken = 0;

  // Stylux.setup() runs in stylux-head.liquid and can finish after this
  // script loads. Until it does, reading Stylux.gql THROWS (it's a getter
  // that raises StyluxSdkSetupError), so every check is wrapped. Without the
  // try, the first early check kills the polling loop and no row ever renders.
  function styluxReady() {
    try {
      var s = window.Stylux;
      return !!(s && s.gql && s.gql.queries && s.modals);
    } catch (e) {
      return false;
    }
  }

  function waitForStylux(timeoutMs) {
    return new Promise(function (resolve) {
      var start = Date.now();
      (function check() {
        if (styluxReady()) return resolve(true);
        if (Date.now() - start > timeoutMs) return resolve(false);
        setTimeout(check, 150);
      })();
    });
  }

  function lookup(variantId) {
    var key = String(variantId);
    if (!productCache.has(key)) {
      var request = window.Stylux.gql.queries
        .productWithOffers({ variables: { id: key, offerChannels: ['D2C'] } })
        .then(function (res) {
          return (res && res.product) || null;
        })
        .catch(function () {
          productCache.delete(key);
          return null;
        });
      productCache.set(key, request);
    }
    return productCache.get(key);
  }

  function activeOffer(product) {
    var nodes = (product && product.liveOffers && product.liveOffers.nodes) || [];
    for (var i = 0; i < nodes.length; i++) {
      var offer = nodes[i];
      if (offer.channel && offer.channel !== 'D2C') continue;
      if (ACTIVE_STATES.indexOf(offer.activeState) === -1) continue;
      return offer;
    }
    return null;
  }

  // The matched tag's variant id for a cart line, or null if it doesn't qualify.
  function matchedTagId(variantId) {
    return lookup(variantId).then(function (product) {
      var offer = activeOffer(product);
      var assoc = (offer && offer.associatedProducts && offer.associatedProducts.nodes) || [];
      for (var i = 0; i < assoc.length; i++) {
        if (assoc[i].type === 'UPSELL' && assoc[i].product && assoc[i].product.productId) {
          return String(assoc[i].product.productId);
        }
      }
      return null;
    });
  }

  function formatMoney(cents) {
    if (window.Rebuy && window.Rebuy.util && typeof window.Rebuy.util.formatMoney === 'function') {
      return window.Rebuy.util.formatMoney(cents);
    }
    return '$ ' + (cents / 100).toFixed(2);
  }

  function cleanTitle(title) {
    return String(title || '').replace(/\s+-\s+Default Title$/, '');
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (name) {
      if (name === 'text') node.textContent = attrs[name];
      else node.setAttribute(name, attrs[name]);
    });
    (children || []).forEach(function (child) {
      if (child) node.appendChild(child);
    });
    return node;
  }

  // Markup mirrors Rebuy's cross-sell rows ("You May Also Like") so the row
  // inherits the same layout, type and secondary button styles.
  function rowFor(tag) {
    var title = cleanTitle(tag.product.title);
    var image = tag.product.images && tag.product.images.nodes && tag.product.images.nodes[0];
    var price = formatMoney(Math.round(Number(tag.product.price) * 100));

    return el('div', { class: 'rebuy-product-block', role: 'listitem', 'aria-label': 'product' }, [
      el('div', { class: 'rebuy-product-media' }, [
        image && image.imageUrl
          ? el('span', { class: 'rebuy-product-image' }, [
              el('img', { src: image.imageUrl, alt: image.altText || title, loading: 'lazy' })
            ])
          : null
      ]),
      el('div', { class: 'rebuy-product-info' }, [
        el('h5', { class: 'rebuy-product-title' }, [el('span', { class: 'rebuy-product-title-link', text: title })]),
        el('div', { class: 'rebuy-product-price' }, [
          el('div', {}, [
            el('span', { class: 'rebuy-money' }, [
              el('span', { class: 'sr-only', text: 'Price' }),
              el('span', { text: ' ' + price })
            ])
          ])
        ])
      ]),
      el('div', { class: 'rebuy-product-actions' }, [
        el('button', {
          type: 'button',
          class: 'rebuy-button',
          'data-mon-id-tag': tag.id,
          'aria-label': 'Personalize and add ' + title + ' to cart'
        }, [el('span', { text: 'Add' })])
      ])
    ]);
  }

  function paint(container, tags) {
    container.textContent = '';
    if (!tags.length) return;

    var grid = el('div', {
      class: 'rebuy-product-grid large-style-line large-columns-1 medium-style-line medium-columns-1 small-style-line small-columns-1',
      role: 'list'
    }, tags.map(rowFor));

    container.appendChild(
      el('div', { class: 'rebuy-widget widget-layout-line is-visible' }, [
        el('div', { class: 'rebuy-widget-container is-visible' }, [
          el('div', { class: 'rebuy-widget-content' }, [
            el('h4', { class: 'primary-title', text: HEADING }),
            grid
          ])
        ])
      ])
    );
  }

  // Keep our container directly after the cart items list. Returns null when
  // the drawer isn't in the DOM yet.
  function ensureContainer() {
    var items = document.querySelector('#rebuy-cart [data-rebuy-component="cart-items"]');
    if (!items || !items.parentNode) return null;

    var container = document.getElementById(CONTAINER_ID);
    if (!container) {
      container = el('div', { id: CONTAINER_ID, class: 'mon-id-tag-offer' });
    }
    if (items.nextElementSibling !== container) {
      items.parentNode.insertBefore(container, items.nextSibling);
    }
    return container;
  }

  function render() {
    if (!ensureContainer()) return;
    var token = ++renderToken;

    fetch('/cart.js', { headers: { accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var inCart = new Set(items.map(function (item) { return String(item.variant_id); }));
        var variantIds = Array.from(new Set(items.map(function (item) { return String(item.variant_id); })));

        return Promise.all(variantIds.map(matchedTagId)).then(function (tagIds) {
          var wanted = [];
          tagIds.forEach(function (id) {
            if (id && !inCart.has(id) && wanted.indexOf(id) === -1) wanted.push(id);
          });
          return Promise.all(wanted.map(function (id) {
            return lookup(id).then(function (product) {
              var offer = activeOffer(product);
              return product && offer ? { id: id, product: product, offer: offer } : null;
            });
          }));
        });
      })
      .then(function (tags) {
        if (token !== renderToken) return; // a newer cart change won
        var target = ensureContainer();
        if (target) paint(target, tags.filter(Boolean));
      })
      .catch(function () {
        if (token !== renderToken) return;
        var target = ensureContainer();
        if (target) target.textContent = '';
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-mon-id-tag]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;

    button.disabled = true;
    lookup(button.getAttribute('data-mon-id-tag'))
      .then(function (product) {
        var offer = activeOffer(product);
        if (!product || !offer) return;
        return window.Stylux.modals.open({ offer: offer, product: product, openingSource: OPENING_SOURCE });
      })
      .catch(function () {})
      .then(function () { button.disabled = false; });
  });

  waitForStylux(20000).then(function (ready) {
    if (!ready) return;

    document.addEventListener('rebuy:cart.change', render);
    document.addEventListener('rebuy:smartcart.show', render);
    document.addEventListener('cart:change', function () { setTimeout(render); });

    render();
  });
})();
